/**
 * HolidaysSettingsPage
 *
 * Admin-Konfiguration für Feiertage & Schulferien-Erinnerungen.
 * Schweizer Feiertage und ZH-Schulferien sind beim ersten Start
 * geseedet, können hier aber editiert, gelöscht oder ergänzt werden.
 *
 * Backend:
 *   GET/POST/PUT/DELETE  /api/admin/holidays
 *   GET/POST/PUT/DELETE  /api/admin/school-vacations
 *   GET/PUT              /api/admin/holiday-reminder-settings
 *   POST                 /api/admin/holiday-reminders/run-now
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Save, Play, AlertCircle, ArrowLeft, Download, Building2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear + 1, currentYear + 2];
const ALL_STUDIOS = '__all__';

const fmtDateCH = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const TYPE_LABELS = {
  federal: { label: 'Bundesfeiertag', color: 'bg-red-100 text-red-700' },
  cantonal: { label: 'Kantonal', color: 'bg-orange-100 text-orange-700' },
  cantonal_zh: { label: 'Kantonal ZH', color: 'bg-orange-100 text-orange-700' },
  cantonal_sg: { label: 'Kantonal SG', color: 'bg-orange-100 text-orange-700' },
  school: { label: 'Schulferien', color: 'bg-blue-100 text-blue-700' },
  school_zh: { label: 'Schulferien ZH', color: 'bg-blue-100 text-blue-700' },
  custom: { label: 'Eigener Feiertag', color: 'bg-purple-100 text-purple-700' },
};

export default function HolidaysSettingsPage({ embedded = false }) {
  const [tab, setTab] = useState('holidays');
  const [holidays, setHolidays] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  // Feiertag-Maske (ein Fenster: Name + Datum + Studio)
  const [holidayModal, setHolidayModal] = useState({ open: false, name: '', date: '', studio_id: ALL_STUDIOS });
  // Schulferien-Maske (ein Fenster: Name + Beginn + Ende + Studio)
  const [vacationModal, setVacationModal] = useState({ open: false, name: '', start: '', end: '', studio_id: ALL_STUDIOS });
  // Studios für Zuordnung
  const [studios, setStudios] = useState([]);
  const [holidayStudioFilter, setHolidayStudioFilter] = useState(ALL_STUDIOS);
  // Schweizer-Feiertage-Import
  const [importModal, setImportModal] = useState({ open: false });
  const [cantons, setCantons] = useState([]);
  const [importCanton, setImportCanton] = useState('CH');
  const [importYear, setImportYear] = useState(String(currentYear));
  const [importStudio, setImportStudio] = useState(ALL_STUDIOS);
  const [catalog, setCatalog] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  // Schweizer-Schulferien-Import
  const [schoolModal, setSchoolModal] = useState({ open: false });
  const [schoolCantons, setSchoolCantons] = useState([]);
  const [schoolCanton, setSchoolCanton] = useState('ZH');
  const [schoolYear, setSchoolYear] = useState(String(currentYear));
  const [schoolStudio, setSchoolStudio] = useState(ALL_STUDIOS);
  const [schoolCatalog, setSchoolCatalog] = useState([]);
  const [selectedStarts, setSelectedStarts] = useState([]);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [schoolImporting, setSchoolImporting] = useState(false);
  // Löschen-Bestätigung
  const [deleteTarget, setDeleteTarget] = useState(null); // {kind:'holiday'|'vacation', id, name}

  const studioName = useCallback((id) => {
    if (!id) return 'Alle Studios';
    const s = studios.find((x) => x.id === id);
    return s ? s.name : 'Studio';
  }, [studios]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [h, v, s] = await Promise.all([
        axios.get(`${API}/admin/holidays`, auth()),
        axios.get(`${API}/admin/school-vacations`, auth()),
        axios.get(`${API}/admin/holiday-reminder-settings`, auth()),
      ]);
      setHolidays(h.data || []);
      setVacations(v.data || []);
      setSettings(s.data);
    } catch (e) {
      toast.error('Fehler beim Laden: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
    try {
      const st = await axios.get(`${API}/studios`, auth());
      setStudios((st.data || []).map((x) => ({ id: x.id, name: x.name })));
    } catch (e) {
      setStudios([]);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/holiday-reminder-settings`, settings, auth());
      toast.success('Einstellungen gespeichert');
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const addHoliday = async () => {
    const { name, date, studio_id } = holidayModal;
    if (!name.trim() || !date) {
      toast.error('Bitte Name und Datum angeben');
      return;
    }
    try {
      await axios.post(`${API}/admin/holidays`, {
        name: name.trim(), date, type: 'custom', reminder_enabled: true,
        studio_id: studio_id === ALL_STUDIOS ? null : studio_id,
      }, auth());
      toast.success('Feiertag hinzugefügt');
      setHolidayModal({ open: false, name: '', date: '', studio_id: ALL_STUDIOS });
      fetchAll();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { kind, id } = deleteTarget;
    const url = kind === 'holiday' ? `holidays/${id}` : `school-vacations/${id}`;
    try {
      await axios.delete(`${API}/admin/${url}`, auth());
      toast.success('Gelöscht');
      fetchAll();
    } catch (e) {
      toast.error('Fehler: ' + e.message);
    } finally {
      setDeleteTarget(null);
    }
  };

  const toggleHolidayReminder = async (h) => {
    try {
      await axios.put(`${API}/admin/holidays/${h.id}`, {
        reminder_enabled: !h.reminder_enabled,
      }, auth());
      fetchAll();
    } catch (e) {
      toast.error('Fehler: ' + e.message);
    }
  };

  const addVacation = async () => {
    const { name, start, end, studio_id } = vacationModal;
    if (!name.trim() || !start || !end) {
      toast.error('Bitte Name, Beginn und Ende angeben');
      return;
    }
    if (end < start) {
      toast.error('Das Ende darf nicht vor dem Beginn liegen');
      return;
    }
    try {
      await axios.post(`${API}/admin/school-vacations`, {
        name: name.trim(), start, end, type: 'custom', reminder_enabled: true,
        studio_id: studio_id === ALL_STUDIOS ? null : studio_id,
      }, auth());
      toast.success('Ferien hinzugefügt');
      setVacationModal({ open: false, name: '', start: '', end: '', studio_id: ALL_STUDIOS });
      fetchAll();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- Schweizer Feiertage importieren ----
  const openImport = async () => {
    setImportModal({ open: true });
    if (cantons.length === 0) {
      try {
        const r = await axios.get(`${API}/admin/holidays/swiss-cantons`, auth());
        setCantons(r.data || []);
      } catch (e) {
        toast.error('Fehler beim Laden der Orte: ' + (e.response?.data?.detail || e.message));
      }
    }
    loadCatalog(importCanton, importYear);
  };

  const loadCatalog = async (canton, year) => {
    setCatalogLoading(true);
    try {
      const r = await axios.get(`${API}/admin/holidays/swiss-catalog`, {
        ...auth(), params: { canton, year },
      });
      const list = r.data || [];
      setCatalog(list);
      // Standard: alle noch nicht vorhandenen vorselektieren
      setSelectedDates(list.filter((x) => !x.already_added).map((x) => x.date));
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCatalogLoading(false);
    }
  };

  const toggleSelectDate = (date) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const runImport = async () => {
    if (selectedDates.length === 0) {
      toast.error('Bitte mindestens einen Feiertag auswählen');
      return;
    }
    setImporting(true);
    try {
      const r = await axios.post(`${API}/admin/holidays/import-swiss`, {
        year: parseInt(importYear, 10),
        canton: importCanton,
        dates: selectedDates,
        studio_id: importStudio === ALL_STUDIOS ? null : importStudio,
      }, auth());
      toast.success(`${r.data.imported} Feiertage importiert${r.data.skipped ? `, ${r.data.skipped} bereits vorhanden` : ''}`);
      setImportModal({ open: false });
      fetchAll();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  // ---- Schweizer Schulferien importieren ----
  const openSchoolImport = async () => {
    setSchoolModal({ open: true });
    if (schoolCantons.length === 0) {
      try {
        const r = await axios.get(`${API}/admin/school-vacations/swiss-cantons`, auth());
        setSchoolCantons(r.data || []);
      } catch (e) {
        toast.error('Fehler beim Laden der Orte: ' + (e.response?.data?.detail || e.message));
      }
    }
    loadSchoolCatalog(schoolCanton, schoolYear);
  };

  const loadSchoolCatalog = async (canton, year) => {
    setSchoolLoading(true);
    try {
      const r = await axios.get(`${API}/admin/school-vacations/swiss-catalog`, {
        ...auth(), params: { canton, year },
      });
      const list = r.data || [];
      setSchoolCatalog(list);
      setSelectedStarts(list.filter((x) => !x.already_added).map((x) => x.start));
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSchoolLoading(false);
    }
  };

  const toggleSelectStart = (start) => {
    setSelectedStarts((prev) =>
      prev.includes(start) ? prev.filter((d) => d !== start) : [...prev, start]
    );
  };

  const runSchoolImport = async () => {
    if (selectedStarts.length === 0) {
      toast.error('Bitte mindestens eine Ferienperiode auswählen');
      return;
    }
    setSchoolImporting(true);
    try {
      const r = await axios.post(`${API}/admin/school-vacations/import-swiss`, {
        year: parseInt(schoolYear, 10),
        canton: schoolCanton,
        starts: selectedStarts,
        studio_id: schoolStudio === ALL_STUDIOS ? null : schoolStudio,
      }, auth());
      toast.success(`${r.data.imported} Ferien importiert${r.data.skipped ? `, ${r.data.skipped} bereits vorhanden` : ''}`);
      setSchoolModal({ open: false });
      fetchAll();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSchoolImporting(false);
    }
  };

  const toggleVacationReminder = async (v) => {
    try {
      await axios.put(`${API}/admin/school-vacations/${v.id}`, {
        reminder_enabled: !v.reminder_enabled,
      }, auth());
      fetchAll();
    } catch (e) {
      toast.error('Fehler: ' + e.message);
    }
  };

  const runNow = async () => {
    if (!window.confirm('Erinnerungs-Lauf jetzt manuell auslösen?')) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/holiday-reminders/run-now`, {}, auth());
      const d = r.data;
      toast.success(
        `${d.targets} relevante Termine, ${d.notified} Kunden benachrichtigt (${d.appointments} betroffene Termine)`
      );
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">Lade Feiertage & Ferien …</div>
    );
  }

  return (
    <div className="p-8 max-w-5xl" data-testid="holidays-settings-page">
      {!embedded && (
        <a href="/settings" data-testid="holidays-back-btn" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Zurück zu Einstellungen
        </a>
      )}
      <div className="flex items-center gap-2 mb-6">
        <Calendar className="h-6 w-6 text-purple-600" />
        <h1 className="text-2xl font-bold">Feiertage & Ferien-Erinnerungen</h1>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Einstellungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Vorlaufzeit Feiertage (Tage)</Label>
              <Input
                type="number" min="0" max="60"
                value={settings.holiday_lead_time_days}
                onChange={(e) => setSettings({ ...settings, holiday_lead_time_days: parseInt(e.target.value) || 0 })}
                data-testid="holiday-lead-input"
              />
              <p className="text-xs text-gray-500 mt-1">Empfohlen: 7 Tage</p>
            </div>
            <div>
              <Label>Vorlaufzeit Schulferien (Tage)</Label>
              <Input
                type="number" min="0" max="90"
                value={settings.vacation_lead_time_days}
                onChange={(e) => setSettings({ ...settings, vacation_lead_time_days: parseInt(e.target.value) || 0 })}
                data-testid="vacation-lead-input"
              />
              <p className="text-xs text-gray-500 mt-1">Empfohlen: 14–21 Tage</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            {[
              ['holiday_reminders_enabled', 'Feiertags-Erinnerungen aktiv'],
              ['vacation_reminders_enabled', 'Ferien-Erinnerungen aktiv'],
              ['send_via_email', 'Versand per E-Mail'],
              ['send_via_whatsapp', 'Versand per WhatsApp'],
              ['send_via_push', 'Versand per Push'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-3">
                <Switch
                  checked={!!settings[key]}
                  onCheckedChange={(v) => setSettings({ ...settings, [key]: v })}
                  data-testid={`switch-${key}`}
                />
                <Label className="text-sm">{label}</Label>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Speichere …' : 'Speichern'}
            </Button>
            <Button onClick={runNow} disabled={running} variant="outline" data-testid="run-now-btn">
              <Play className="h-4 w-4 mr-2" />
              {running ? 'Läuft …' : 'Jetzt manuell auslösen'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="holidays" data-testid="tab-holidays">
            Feiertage ({holidays.length})
          </TabsTrigger>
          <TabsTrigger value="vacations" data-testid="tab-vacations">
            Schulferien ({vacations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="holidays">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Feiertage</CardTitle>
              <div className="flex gap-2">
                {studios.length > 0 && (
                  <Select value={holidayStudioFilter} onValueChange={setHolidayStudioFilter}>
                    <SelectTrigger className="h-9 w-[180px]" data-testid="holiday-studio-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_STUDIOS}>Alle anzeigen</SelectItem>
                      <SelectItem value="__global__">Nur globale</SelectItem>
                      {studios.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button size="sm" variant="outline" onClick={openImport} data-testid="import-swiss-btn">
                  <Download className="h-4 w-4 mr-1" /> Schweizer Feiertage
                </Button>
                <Button size="sm" onClick={() => setHolidayModal({ open: true, name: '', date: '', studio_id: ALL_STUDIOS })} data-testid="add-holiday-btn">
                  <Plus className="h-4 w-4 mr-1" /> Neuer Feiertag
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {holidays
                  .filter((h) => holidayStudioFilter === ALL_STUDIOS
                    ? true
                    : holidayStudioFilter === '__global__'
                      ? !h.studio_id
                      : h.studio_id === holidayStudioFilter)
                  .map((h) => {
                  const t = TYPE_LABELS[h.type] || TYPE_LABELS.custom;
                  return (
                    <div
                      key={h.id}
                      className="flex items-center justify-between p-3 border rounded bg-white"
                      data-testid={`holiday-${h.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={h.reminder_enabled}
                          onCheckedChange={() => toggleHolidayReminder(h)}
                          data-testid={`toggle-holiday-${h.id}`}
                        />
                        <div>
                          <div className="font-medium">{h.name}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(h.date).toLocaleDateString('de-CH', {
                              weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric',
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {h.studio_id && (
                          <Badge variant="outline" className="gap-1 text-gray-600" data-testid={`holiday-studio-${h.id}`}>
                            <Building2 className="h-3 w-3" /> {studioName(h.studio_id)}
                          </Badge>
                        )}
                        <Badge className={t.color}>{t.label}</Badge>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteTarget({ kind: 'holiday', id: h.id, name: h.name })}
                          data-testid={`delete-holiday-${h.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {holidays.filter((h) => holidayStudioFilter === ALL_STUDIOS
                  ? true
                  : holidayStudioFilter === '__global__'
                    ? !h.studio_id
                    : h.studio_id === holidayStudioFilter).length === 0 && (
                  <div className="text-sm text-gray-500 text-center py-6">
                    {holidays.length === 0 ? 'Noch keine Feiertage. Klicke auf "Neuer Feiertag".' : 'Keine Feiertage für diesen Standort.'}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vacations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Schulferien</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openSchoolImport} data-testid="import-school-btn">
                  <Download className="h-4 w-4 mr-1" /> Schweizer Schulferien
                </Button>
                <Button size="sm" onClick={() => setVacationModal({ open: true, name: '', start: '', end: '', studio_id: ALL_STUDIOS })} data-testid="add-vacation-btn">
                  <Plus className="h-4 w-4 mr-1" /> Neue Ferien
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {vacations.map((v) => {
                  const t = TYPE_LABELS[v.type] || TYPE_LABELS.custom;
                  return (
                    <div
                      key={v.id}
                      className="flex items-center justify-between p-3 border rounded bg-white"
                      data-testid={`vacation-${v.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={v.reminder_enabled}
                          onCheckedChange={() => toggleVacationReminder(v)}
                          data-testid={`toggle-vacation-${v.id}`}
                        />
                        <div>
                          <div className="font-medium">{v.name}</div>
                          <div className="text-xs text-gray-500">
                            {fmtDateCH(v.start)}
                            {' – '}
                            {fmtDateCH(v.end)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {v.studio_id && (
                          <Badge variant="outline" className="gap-1 text-gray-600" data-testid={`vacation-studio-${v.id}`}>
                            <Building2 className="h-3 w-3" /> {studioName(v.studio_id)}
                          </Badge>
                        )}
                        <Badge className={t.color}>{t.label}</Badge>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteTarget({ kind: 'vacation', id: v.id, name: v.name })}
                          data-testid={`delete-vacation-${v.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {vacations.length === 0 && (
                  <div className="text-sm text-gray-500 text-center py-6">
                    Noch keine Schulferien. Klicke auf &quot;Neue Ferien&quot;.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded">
        <div className="flex gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-1">So funktioniert der automatische Versand</p>
            <p>
              Jeden Tag um <strong>08:00</strong> prüft das System, ob ein Feiertag in
              exakt <strong>{settings.holiday_lead_time_days} Tagen</strong> oder eine Schulferien-Periode in
              exakt <strong>{settings.vacation_lead_time_days} Tagen</strong> beginnt.
              Kunden mit Terminen in diesem Zeitraum erhalten dann automatisch eine Erinnerung
              {settings.send_via_email && ' per E-Mail'}
              {settings.send_via_push && (settings.send_via_email ? ', Push' : ' per Push')}
              {settings.send_via_whatsapp && ', WhatsApp'}.
              Jeder Kunde erhält pro Feiertag/Ferien maximal eine Erinnerung pro Jahr.
            </p>
          </div>
        </div>
      </div>

      {/* Feiertag-Maske: Name + Datum in einem Fenster */}
      <Dialog open={holidayModal.open} onOpenChange={(o) => setHolidayModal((m) => ({ ...m, open: o }))}>
        <DialogContent data-testid="holiday-modal">
          <DialogHeader>
            <DialogTitle>Neuer Feiertag</DialogTitle>
            <DialogDescription>Name und Datum in einem Schritt erfassen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name des Feiertags</Label>
              <Input
                value={holidayModal.name}
                onChange={(e) => setHolidayModal((m) => ({ ...m, name: e.target.value }))}
                placeholder="z.B. Betriebsferien"
                data-testid="holiday-name-input"
              />
            </div>
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={holidayModal.date}
                onChange={(e) => setHolidayModal((m) => ({ ...m, date: e.target.value }))}
                data-testid="holiday-date-input"
              />
              {holidayModal.date && (
                <p className="text-xs text-gray-500 mt-1">{fmtDateCH(holidayModal.date)}</p>
              )}
            </div>
            {studios.length > 0 && (
              <div>
                <Label>Gilt für</Label>
                <Select
                  value={holidayModal.studio_id}
                  onValueChange={(val) => setHolidayModal((m) => ({ ...m, studio_id: val }))}
                >
                  <SelectTrigger data-testid="holiday-studio-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_STUDIOS}>Alle Studios</SelectItem>
                    {studios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Nur Kunden des gewählten Studios erhalten die Erinnerung.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayModal({ open: false, name: '', date: '', studio_id: ALL_STUDIOS })}>Abbrechen</Button>
            <Button onClick={addHoliday} data-testid="holiday-save-btn">Hinzufügen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schulferien-Maske: Name + Beginn + Ende in einem Fenster */}
      <Dialog open={vacationModal.open} onOpenChange={(o) => setVacationModal((m) => ({ ...m, open: o }))}>
        <DialogContent data-testid="vacation-modal">
          <DialogHeader>
            <DialogTitle>Neue Schulferien</DialogTitle>
            <DialogDescription>Name und Zeitraum in einem Schritt erfassen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name der Ferien</Label>
              <Input
                value={vacationModal.name}
                onChange={(e) => setVacationModal((m) => ({ ...m, name: e.target.value }))}
                placeholder='z.B. "Frühlingsferien"'
                data-testid="vacation-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Beginn</Label>
                <Input
                  type="date"
                  value={vacationModal.start}
                  onChange={(e) => setVacationModal((m) => ({ ...m, start: e.target.value }))}
                  data-testid="vacation-start-input"
                />
                {vacationModal.start && (
                  <p className="text-xs text-gray-500 mt-1">{fmtDateCH(vacationModal.start)}</p>
                )}
              </div>
              <div>
                <Label>Ende</Label>
                <Input
                  type="date"
                  value={vacationModal.end}
                  onChange={(e) => setVacationModal((m) => ({ ...m, end: e.target.value }))}
                  data-testid="vacation-end-input"
                />
                {vacationModal.end && (
                  <p className="text-xs text-gray-500 mt-1">{fmtDateCH(vacationModal.end)}</p>
                )}
              </div>
            </div>
            {studios.length > 0 && (
              <div>
                <Label>Gilt für</Label>
                <Select
                  value={vacationModal.studio_id}
                  onValueChange={(val) => setVacationModal((m) => ({ ...m, studio_id: val }))}
                >
                  <SelectTrigger data-testid="vacation-studio-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_STUDIOS}>Alle Studios</SelectItem>
                    {studios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Nur Kunden des gewählten Studios erhalten die Erinnerung.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVacationModal({ open: false, name: '', start: '', end: '', studio_id: ALL_STUDIOS })}>Abbrechen</Button>
            <Button onClick={addVacation} data-testid="vacation-save-btn">Hinzufügen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schweizer Feiertage importieren: Ort wählen, Daten finden, alle oder einzelne übernehmen */}
      <Dialog open={importModal.open} onOpenChange={(o) => setImportModal({ open: o })}>
        <DialogContent data-testid="import-swiss-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schweizer Feiertage importieren</DialogTitle>
            <DialogDescription>
              Ort und Jahr wählen – wir finden die gültigen Feiertage. Alle oder nur einzelne übernehmen.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ort / Kanton</Label>
              <Select
                value={importCanton}
                onValueChange={(val) => { setImportCanton(val); loadCatalog(val, importYear); }}
              >
                <SelectTrigger data-testid="import-canton-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cantons.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jahr</Label>
              <Select
                value={importYear}
                onValueChange={(val) => { setImportYear(val); loadCatalog(importCanton, val); }}
              >
                <SelectTrigger data-testid="import-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {studios.length > 0 && (
            <div className="mt-3">
              <Label>Importieren für</Label>
              <Select value={importStudio} onValueChange={setImportStudio}>
                <SelectTrigger data-testid="import-studio-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STUDIOS}>Alle Studios</SelectItem>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">{selectedDates.length} ausgewählt</span>
            <div className="flex gap-3">
              <button
                type="button"
                className="text-xs text-purple-600 hover:underline"
                onClick={() => setSelectedDates(catalog.map((x) => x.date))}
                data-testid="import-select-all"
              >Alle</button>
              <button
                type="button"
                className="text-xs text-gray-500 hover:underline"
                onClick={() => setSelectedDates([])}
                data-testid="import-select-none"
              >Keine</button>
            </div>
          </div>

          <ScrollArea className="h-64 border rounded mt-1">
            {catalogLoading ? (
              <div className="p-6 text-center text-sm text-gray-500">Lade Feiertage …</div>
            ) : catalog.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">Keine Feiertage gefunden.</div>
            ) : (
              <div className="divide-y">
                {catalog.map((it) => (
                  <label
                    key={it.date}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                    data-testid={`import-row-${it.date}`}
                  >
                    <Checkbox
                      checked={selectedDates.includes(it.date)}
                      onCheckedChange={() => toggleSelectDate(it.date)}
                      disabled={it.already_added}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{it.name}</div>
                      <div className="text-xs text-gray-500">{fmtDateCH(it.date)}</div>
                    </div>
                    {it.already_added && (
                      <Badge className="bg-gray-100 text-gray-500">bereits vorhanden</Badge>
                    )}
                  </label>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportModal({ open: false })}>Abbrechen</Button>
            <Button onClick={runImport} disabled={importing || selectedDates.length === 0} data-testid="import-confirm-btn">
              {importing ? 'Importiere …' : `${selectedDates.length} übernehmen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schweizer Schulferien importieren */}
      <Dialog open={schoolModal.open} onOpenChange={(o) => setSchoolModal({ open: o })}>
        <DialogContent data-testid="import-school-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Schweizer Schulferien importieren</DialogTitle>
            <DialogDescription>
              Kanton und Jahr wählen – wir finden die offiziellen Schulferien. Daten sind Richtwerte und nach dem Import editierbar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Kanton</Label>
              <Select
                value={schoolCanton}
                onValueChange={(val) => { setSchoolCanton(val); loadSchoolCatalog(val, schoolYear); }}
              >
                <SelectTrigger data-testid="school-canton-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {schoolCantons.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jahr</Label>
              <Select
                value={schoolYear}
                onValueChange={(val) => { setSchoolYear(val); loadSchoolCatalog(schoolCanton, val); }}
              >
                <SelectTrigger data-testid="school-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {studios.length > 0 && (
            <div className="mt-3">
              <Label>Importieren für</Label>
              <Select value={schoolStudio} onValueChange={setSchoolStudio}>
                <SelectTrigger data-testid="school-studio-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STUDIOS}>Alle Studios</SelectItem>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">{selectedStarts.length} ausgewählt</span>
            <div className="flex gap-3">
              <button
                type="button"
                className="text-xs text-purple-600 hover:underline"
                onClick={() => setSelectedStarts(schoolCatalog.map((x) => x.start))}
                data-testid="school-select-all"
              >Alle</button>
              <button
                type="button"
                className="text-xs text-gray-500 hover:underline"
                onClick={() => setSelectedStarts([])}
                data-testid="school-select-none"
              >Keine</button>
            </div>
          </div>

          <ScrollArea className="h-64 border rounded mt-1">
            {schoolLoading ? (
              <div className="p-6 text-center text-sm text-gray-500">Lade Schulferien …</div>
            ) : schoolCatalog.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">Keine Schulferien gefunden.</div>
            ) : (
              <div className="divide-y">
                {schoolCatalog.map((it) => (
                  <label
                    key={it.start}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                    data-testid={`school-row-${it.start}`}
                  >
                    <Checkbox
                      checked={selectedStarts.includes(it.start)}
                      onCheckedChange={() => toggleSelectStart(it.start)}
                      disabled={it.already_added}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{it.name}</div>
                      <div className="text-xs text-gray-500">{fmtDateCH(it.start)} – {fmtDateCH(it.end)}</div>
                    </div>
                    {it.already_added && (
                      <Badge className="bg-gray-100 text-gray-500">bereits vorhanden</Badge>
                    )}
                  </label>
                ))}
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSchoolModal({ open: false })}>Abbrechen</Button>
            <Button onClick={runSchoolImport} disabled={schoolImporting || selectedStarts.length === 0} data-testid="school-confirm-btn">
              {schoolImporting ? 'Importiere …' : `${selectedStarts.length} übernehmen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Löschen bestätigen */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.kind === 'vacation' ? 'Schulferien löschen?' : 'Feiertag löschen?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              „{deleteTarget?.name}“ wird dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
